import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import Swal from "sweetalert2";

import ResumenEjecutivo from "./ResumenEjecutivo";
import TarjetaInfoGeneral from "./TarjetaInfoGeneral";
import TarjetaRuta from "./TarjetaRuta";
import DialogCombinable from "./DialogCombinable";
import DialogAnalisisIA from "./DialogAnalisisIA";

import { getVisibilidad } from "../estatusVisibilidad";
import { ESTATUS_CONFIG, DIAS_SEMANA } from "../ESTATUS_CONFIG";
import {
    desmarcarCombinable,
    desmarcarPrecioReferencia,
    marcarCombinable,
    marcarPrecioReferencia,
    programarActualizacionCotizacion,
    sendToClient,
    verifyRouteData,
    verifyStrokesData
} from "../Integracion";
import { sendToProduction, sendToProductionMod } from "../../NuevaCotizacion/integracion";

import usePermisosModulo from "../../../../utilidades/customHooks/usePermisosModulo";
import { confirmarConPalabraClave } from "../../../../utilidades/confirmarAccion";

import "../DetalleCotizacion.scss";
import {formatearFechaHora} from "../../../../utilidades/formateos";
import ComentariosCotizacion from "./ComentariosCotizacion";

/**
 * Vista de detalle de una cotización empresarial.
 * NOTA: cotizacion.rutas es un OBJETO (una sola ruta), no un array.
 */
const DetallesComponent = ({
                               cotizacion = null,
                               setCotizacion,
                               cargando = false,
                               error = null,
                               onRegresar,
                               onEditar,
                               onEditarMod,
                               onGenerarTrazo,
                               handleEditarAllTrazos,
                               onEditarTrazo,
                           }) => {
    const { tienePermiso, userId } = usePermisosModulo(
        "Cotizaciones empresariales",
        "Listado de cotizaciones"
    );

    const estatusActual = useMemo(() => {
        if (!cotizacion) return null;
        return ESTATUS_CONFIG[cotizacion.estatus] || ESTATUS_CONFIG[1];
    }, [cotizacion]);

    const visibilidad = useMemo(() => {
        if (!cotizacion) return getVisibilidad(1);
        return getVisibilidad(cotizacion.estatus);
    }, [cotizacion]);

    const estatusBloqueado = cotizacion?.estatus === 7;
    const esReferencia = cotizacion?.tipoCotizacion === "REFERENCIA";
    const esCombinable = Boolean(cotizacion?.esCombinable);
    const [dialogCombinableVisible, setDialogCombinableVisible] = useState(false);

    // Si es una modificación (existe rutaAnterior) y el precio cambió pero el
    // pago al chofer sigue igual que antes, significa que no se recalculó.
    const esModificacion = Boolean(cotizacion?.rutaAnterior);
    const precioCambio = esModificacion && cotizacion.rutas?.precioCliente !== cotizacion.rutaAnterior.precioCliente;
    const pagoChoferDesactualizado = precioCambio && cotizacion.rutas?.pagoChofer === cotizacion.rutaAnterior.pagoChofer;

    // El botón solo se bloquea cuando falta capturar alguno de los dos pagos
    // (null o 0). "Desactualizado" es un aviso aparte, no impide el envío.
    const pagoChoferValido = Boolean(cotizacion?.rutas?.pagoChofer);
    const pagoChoferSencilloValido = Boolean(cotizacion?.rutas?.pagoChoferSencillo);
    const costosDefinidos = pagoChoferValido && pagoChoferSencilloValido;
    const mensajeCostosPendientes = (!pagoChoferValido || !pagoChoferSencilloValido)
        ? "Falta definir el pago al chofer y/o el pago al chofer sencillo"
        : "No olvides verificar si es necesario actualizar el pago al operador.";

    // Compara los pagos capturados contra el pago sugerido calculado para la ruta.
    const pagoChoferNoConcuerda =
        cotizacion?.rutas?.pagoCorrespondiente != null &&
        cotizacion.rutas.pagoChofer !== cotizacion.rutas.pagoCorrespondiente;
    const pagoChoferSencilloNoConcuerda =
        cotizacion?.rutas?.pagoCorrespondienteS != null &&
        cotizacion.rutas.pagoChoferSencillo !== cotizacion.rutas.pagoCorrespondienteS;
    const pagosNoConcuerdanConSugerido = pagoChoferNoConcuerda || pagoChoferSencilloNoConcuerda;

    const permisosFlags = {
        puedeMandarProduccion: !estatusBloqueado && tienePermiso("MANDAR A PRODUCCIÓN"),
        puedeConfirmarDatos:   !estatusBloqueado && tienePermiso("CONFIRMAR DATOS"),
        puedeVerificarTrazos:  !estatusBloqueado && tienePermiso("VERIFICAR TRAZOS"),
        puedeDefinirCostos:    !estatusBloqueado && tienePermiso("DEFINIR COSTOS"),
        puedeEnviarCliente:    !estatusBloqueado && tienePermiso("ENVIAR AL CLIENTE"),
        puedeMarcarReferencia: !estatusBloqueado && tienePermiso("MARCAR PRECIO REFERENCIA"),
        puedeMarcarCombinable: !estatusBloqueado && tienePermiso("MARCAR PRECIO REFERENCIA"),
    };

    /**
     * Verifica los datos principales de la ruta y avanza a "En Planeación".
     */
    const handleVerificar = () =>
        confirmarConPalabraClave({
            titulo: "¿Seguro que quieres verificar los datos?",
            palabra: "verificar",
            html: `
                <p style="margin-bottom:10px;">
                    Al confirmar esta acción, la información asociada al estatus actual quedará bloqueada para edición
                    y la ruta pasará al estatus <strong>Planeación</strong> para iniciar el proceso de generación de trazo.
                    Solo los usuarios con permisos para modificar el estatus podrán realizar cambios posteriores.
                </p>
                <p style="margin-bottom:5px;">Escribe la palabra <strong>verificar</strong> para continuar:</p>
                <input id="swal-input-confirm" class="swal2-input" placeholder="verificar" autocomplete="off" />
            `,
            onConfirmar: async () => {
                await verifyRouteData(cotizacion.id);
                setCotizacion((prev) =>
                    prev
                        ? {
                            ...prev,
                            estatus: 2,
                            rutas: {
                                ...prev.rutas,
                                estructuras: prev.rutas.estructuras.map((e) => ({ ...e, verificado: true })),
                            },
                        }
                        : prev
                );
            },
        });

    /**
     * Marca la cotización como precio de referencia (unidad no disponible).
     */
    const handleMarcarReferencia = async () => {
        const motivo = "Unidad no disponible";

        const { isConfirmed } = await Swal.fire({
            title: "¿Marcar esta cotización como precio de referencia?",
            html: `
            <p style="text-align:left;margin-bottom:8px;">La unidad solicitada no está disponible actualmente.</p>
            <p style="text-align:left;margin:0;">
                El precio se enviará al cliente únicamente como referencia y no implica disponibilidad de la unidad.
            </p>
        `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí, marcar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#f97316",
        });
        if (!isConfirmed) return;

        try {
            await marcarPrecioReferencia(cotizacion.id, userId, motivo);
            setCotizacion((prev) => (prev ? { ...prev, tipoCotizacion: "REFERENCIA", motivoReferencia: motivo } : prev));
            await Swal.fire({
                icon: "success",
                title: "Cotización marcada como precio de referencia",
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message ?? "No se pudo marcar la cotización"
            });
        }
    };

    /**
     * Quita la marca de precio de referencia (la unidad ya está disponible).
     */
    const handleDesmarcarReferencia = async () => {
        const { isConfirmed } = await Swal.fire({
            title: "¿Quitar el precio de referencia?",
            text: "La cotización volverá a ser normal y se podrá enviar a producción.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sí, quitar",
            cancelButtonText: "Cancelar",
        });
        if (!isConfirmed) return;

        try {
            await desmarcarPrecioReferencia(cotizacion.id);
            setCotizacion((prev) => (prev ? { ...prev, tipoCotizacion: "NORMAL", motivoReferencia: null } : prev));
            await Swal.fire({
                icon: "success",
                title: "Se quitó el precio de referencia",
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message ?? "No se pudo quitar el precio de referencia"
            });
        }
    };

    /**
     * Marca la cotización como combinable con la ruta elegida en el diálogo.
     */
    const handleMarcarCombinable = async (ruta) => {
        try {
            await marcarCombinable(cotizacion.id, ruta.id);
            setCotizacion((prev) =>
                prev ? { ...prev, esCombinable: true, idRutaCombinada: ruta.id, rutaCombinadaNombre: ruta.nombre } : prev
            );
            setDialogCombinableVisible(false);
            await Swal.fire({
                icon: "success",
                title: "Cotización marcada como combinable",
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message ?? "No se pudo marcar la cotización como combinable"
            });
        }
    };

    /**
     * Quita la marca de combinable.
     */
    const handleDesmarcarCombinable = async () => {
        const { isConfirmed } = await Swal.fire({
            title: "¿Quitar la marca de combinable?",
            text: "La cotización dejará de estar asociada a la ruta combinada.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sí, quitar",
            cancelButtonText: "Cancelar",
        });
        if (!isConfirmed) return;

        try {
            await desmarcarCombinable(cotizacion.id);
            setCotizacion((prev) =>
                prev ? { ...prev, esCombinable: false, idRutaCombinada: null, rutaCombinadaNombre: null } : prev
            );
            await Swal.fire({
                icon: "success",
                title: "Se quitó la marca de combinable",
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message ?? "No se pudo quitar la marca de combinable"
            });
        }
    };

    /**
     * Verifica los trazos y avanza a "En Costeo".
     */
    const handleVerificarTrazo = () =>
        confirmarConPalabraClave({
            titulo: "¿Seguro que quieres verificar los datos?",
            palabra: "verificar",
            tituloError: "Error al verificar los datos",
            html: `
                <p style="margin-bottom:10px;">
                    Al confirmar esta acción, la información correspondiente al estatus actual quedará bloqueada para su edición.
                    La ruta cambiará al estatus <strong>En Costeo</strong>, iniciando el proceso de cálculo de precios.
                    Posteriormente, solo los usuarios con permisos para modificar el estatus podrán realizar cambios.
                </p>
                <p style="margin-bottom:5px;">Escribe la palabra <strong>verificar</strong> para continuar:</p>
                <input id="swal-input-confirm" class="swal2-input" placeholder="verificar" autocomplete="off" />
            `,
            onConfirmar: async () => {
                await verifyStrokesData(cotizacion.id);
                setCotizacion((prev) => (prev ? { ...prev, estatus: 3 } : prev));
            },
        });

    /**
     * Envía la cotización al cliente.
     */
    const handleSendToClient = () => {
        if (!cotizacion?.id) {
            Swal.fire({ icon: "error", title: "Cotización inválida", text: "No se encontró información válida para enviar." });
            return;
        }

        return confirmarConPalabraClave({
            titulo: "Confirmar envío al cliente",
            palabra: "ENVIAR",
            tituloExito: "Cotización enviada correctamente",
            tituloError: "Error al enviar la cotización",
            html: `
                <p style="margin-bottom:10px;">
                    Al confirmar esta acción, la cotización será enviada al cliente para su revisión.
                    A partir de este momento, la información quedará bloqueada y no podrá modificarse
                    hasta que el cliente acepte o rechace la cotización.
                </p>
                <p style="margin-bottom:8px;">Para confirmar, escribe <strong>ENVIAR</strong>:</p>
                <input id="swal-input-confirm" class="swal2-input" placeholder="ENVIAR" autocomplete="off" />
            `,
            onConfirmar: async () => {
                await sendToClient(cotizacion.id);
                setCotizacion((prev) => (prev ? { ...prev, estatus: 4 } : prev));
            },
        });
    };

    /**
     * Envía la cotización a producción. Requiere fecha de inicio del cambio
     * además de la palabra de confirmación, por eso no usa el helper genérico.
     */
    const handleSendToProduction = async () => {

        if (esReferencia) {
            Swal.fire({
                icon: "error",
                title: "No es posible enviar esta cotización a producción.",
                text: "La unidad marcada actualmente no está disponible.",
            });
            return;
        }

        if (pagosNoConcuerdanConSugerido) {
            const confirmacionSugerido = await Swal.fire({
                title: "Los pagos no concuerdan",
                html: `
                    <p style="text-align:left;margin-bottom:0;">
                        El pago al chofer registrado no concuerda con el pago sugerido para esta ruta.
                        ¿Estás seguro de que deseas continuar?
                    </p>
                `,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Sí, continuar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#dc2626",
            });
            if (!confirmacionSugerido.isConfirmed) return;
        }

        const today = new Date().toISOString().split("T")[0];

        const result = await Swal.fire({
            title: "Enviar a producción",
            html: `
                <div style="text-align:left">
                    <p style="margin-bottom:12px;">
                        Esta acción copiará <b>rutas, estructuras, trazos y paradas</b> al sistema de producción
                        y activará los cambios operativos de forma definitiva.
                    </p>
                    <div style="background:#fef9c3;border:1px solid #fde047;border-radius:8px;padding:12px;margin-bottom:16px;">
                        <p style="margin:0 0 8px;font-weight:600;color:#854d0e;">
                            <i class="pi pi-calendar" style="margin-right:6px;"></i> Fecha de inicio del cambio
                        </p>
                        <p style="margin:0 0 10px;font-size:0.875rem;color:#713f12;">
                            Indica a partir de qué fecha entrarán en vigor los cambios en producción.
                        </p>
                        <input id="swal-fecha-produccion" type="date" class="swal2-input" value="${today}" style="margin:0;width:100%;" />
                        <div id="swal-retroactivo-msg" style="display:none;margin-top:10px;padding:8px 12px;background:#fee2e2;border:1px solid #fca5a5;border-radius:6px;color:#b91c1c;font-size:0.85rem;font-weight:600;">
                            ⚠️ La fecha seleccionada ya pasó — este cambio se aplicará de forma <u>retroactiva</u>.
                        </div>
                    </div>
                    <div style="background:#fee2e2;border:1px solid #fca5a5;border-radius:8px;padding:12px;margin-bottom:16px;">
                        <p style="margin:0;color:#b91c1c;font-weight:600;">⚠️ Esta acción es irreversible y no podrá deshacerse.</p>
                    </div>
                    <p style="margin-bottom:8px;">Para confirmar escribe <strong>ACEPTAR</strong>:</p>
                    <input id="swal-confirm-production" class="swal2-input" placeholder="Escribe la palabra ACEPTAR" autocomplete="off" />
                </div>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Enviar a producción",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#dc2626",
            focusConfirm: false,
            didOpen: () => {
                const fechaInput = document.getElementById("swal-fecha-produccion");
                const retroMsg = document.getElementById("swal-retroactivo-msg");
                const checkRetroactivo = () => {
                    const selected = new Date(fechaInput.value);
                    const hoy = new Date();
                    hoy.setHours(0, 0, 0, 0);
                    retroMsg.style.display = selected < hoy ? "block" : "none";
                };
                fechaInput.addEventListener("change", checkRetroactivo);
                checkRetroactivo();
            },
            preConfirm: () => {
                const fecha = document.getElementById("swal-fecha-produccion")?.value;
                const texto = document.getElementById("swal-confirm-production")?.value?.trim();
                if (!fecha) {
                    Swal.showValidationMessage("Debes seleccionar una fecha de inicio del cambio.");
                    return false;
                }
                if (texto !== "ACEPTAR") {
                    Swal.showValidationMessage('Debes escribir exactamente "ACEPTAR" para continuar.');
                    return false;
                }
                return { fecha };
            },
        });

        if (!result.isConfirmed) return;
        const { fecha } = result.value;

        try {
            Swal.fire({ title: "Enviando a producción...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            const fechaSeleccionada = new Date(fecha);

            // Si la fecha seleccionada ya pasó o es hoy, se ejecuta pronto (+5 min de colchón)
            let fechaEjecucion;
            if (fechaSeleccionada > hoy) {
                fechaEjecucion = fecha;
            } else {
                const ejecucionInmediata = new Date();
                ejecucionInmediata.setMinutes(ejecucionInmediata.getMinutes() + 5);
                fechaEjecucion = formatearFechaHora(ejecucionInmediata);
            }

            if (cotizacion.categoria === "NUEVA") {
                await sendToProduction(cotizacion.rutas.id, userId, fecha);
            } else {
                await programarActualizacionCotizacion({
                    idRuta: cotizacion.rutas.id,
                    idUsuario: userId,
                    idCotizacion: cotizacion.id,
                    fechaDesde: fecha,
                    fechaEjecucion,
                });
            }

            setCotizacion((prev) => (prev ? { ...prev, estatus: 6 } : prev));

            await Swal.fire({
                icon: "success",
                title: "Enviado a producción",
                text: "Los datos fueron enviados correctamente",
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(error);
            Swal.fire({ icon: "error", title: "Error al enviar a producción", text: "Ocurrió un problema al procesar la solicitud" });
        }
    };

    const handleSolicitarCambios = () => {
        setCotizacion((prev) => ({ ...prev, estatus: 8 }));
    };

    if (cargando) {
        return (
            <div className="detalle-cotizacion__cargando">
                <ProgressSpinner style={{ width: "60px", height: "60px" }} strokeWidth="4" />
                <p className="mt-3 text-lg">Cargando información de la cotización...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="detalle-cotizacion__error">
                <Message severity="error" text={error} className="w-full mb-3" />
                <Button label="Regresar al listado" icon="pi pi-arrow-left" className="p-button-outlined" onClick={onRegresar} />
            </div>
        );
    }

    if (!cotizacion) {
        return (
            <div className="detalle-cotizacion__error">
                <Message severity="warn" text="No se encontró la cotización solicitada." className="w-full mb-3" />
                <Button label="Regresar al listado" icon="pi pi-arrow-left" className="p-button-outlined" onClick={onRegresar} />
            </div>
        );
    }

    const ruta = cotizacion.rutas;

    return (
        <div className="detalle-cotizacion">
            {/* ════════════ ENCABEZADO ════════════ */}
            <div className="detalle-cotizacion__header">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <Button
                        icon="pi pi-arrow-left"
                        className="p-button-text p-button-rounded p-button-lg"
                        onClick={onRegresar}
                        tooltip="Regresar al listado"
                        tooltipOptions={{ position: "bottom" }}
                    />
                    <div className="flex-1">
                        <h2 className="detalle-cotizacion__titulo m-0">
                            <i className="pi pi-file-edit mr-2" />
                            Cotización #{cotizacion.id}
                        </h2>
                        <span className="detalle-cotizacion__subtitulo">
                            {cotizacion.clienteNombre || `Cliente #${cotizacion.cliente}`}
                        </span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        {esReferencia && (
                            <Tag
                                severity="warning"
                                icon="pi pi-exclamation-triangle"
                                value={`PRECIO DE REFERENCIA — ${cotizacion.motivoReferencia?.toUpperCase() ?? ""}`}
                            />
                        )}
                        {esReferencia && permisosFlags.puedeMarcarReferencia && (
                            <Button
                                label="Quitar precio de referencia"
                                icon="pi pi-times"
                                severity="warning"
                                className="p-button-outlined p-button-sm"
                                onClick={handleDesmarcarReferencia}
                            />
                        )}
                        {!esReferencia && permisosFlags.puedeMarcarReferencia && (
                            <Button
                                label="Marcar como precio de referencia"
                                icon="pi pi-tag"
                                severity="warning"
                                className="p-button-outlined p-button-sm"
                                onClick={handleMarcarReferencia}
                            />
                        )}
                        {esCombinable && (
                            <Tag
                                severity="info"
                                icon="pi pi-link"
                                value={`COMBINABLE — ${cotizacion.rutaCombinadaNombre?.toUpperCase() ?? `RUTA #${cotizacion.idRutaCombinada}`}`}
                            />
                        )}
                        {esCombinable && permisosFlags.puedeMarcarCombinable && (
                            <Button
                                label="Quitar combinable"
                                icon="pi pi-times"
                                severity="info"
                                className="p-button-outlined p-button-sm"
                                onClick={handleDesmarcarCombinable}
                            />
                        )}
                        {!esCombinable && permisosFlags.puedeMarcarCombinable && (
                            <Button
                                label="Marcar como combinable"
                                icon="pi pi-link"
                                severity="info"
                                className="p-button-outlined p-button-sm"
                                onClick={() => setDialogCombinableVisible(true)}
                            />
                        )}
                        <DialogAnalisisIA cotizacion={cotizacion} />
                        {cotizacion?.id && (
                            <ComentariosCotizacion
                                idCotizacion={cotizacion.id}
                                idUsuario={userId}
                                estatus={cotizacion.estatus}
                            />
                        )}
                        <Tag
                            value={estatusActual.label}
                            icon={estatusActual.icon}
                            severity={estatusActual.severity}
                            className="detalle-cotizacion__estatus-tag"
                            style={!estatusActual.severity ? { backgroundColor: estatusActual.color, color: "#fff" } : undefined}
                        />
                        {visibilidad.puedeEditar && (onEditar || onEditarMod) && (
                            <Button
                                label="Editar"
                                icon="pi pi-pencil"
                                className="p-button-outlined p-button-sm"
                                onClick={() => (cotizacion.idCategoria === 5 ? onEditarMod(cotizacion.id) : onEditar(cotizacion.id))}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* ════════════ MENSAJE CONTEXTUAL ════════════ */}
            {visibilidad.mensajeContextual && (
                <div className="detalle-cotizacion__seccion">
                    <Message severity="info" text={visibilidad.mensajeContextual} className="w-full" style={{ borderRadius: "8px" }} />
                </div>
            )}

            {/* ════════════ RESUMEN EJECUTIVO ════════════ */}
            {visibilidad.infoGeneral && ruta && (
                <div className="detalle-cotizacion__seccion">
                    <ResumenEjecutivo cotizacion={cotizacion} visibilidad={visibilidad} />
                </div>
            )}

            {/* ════════════ INFORMACIÓN GENERAL ════════════ */}
            {visibilidad.infoGeneral && (
                <div className="detalle-cotizacion__seccion">
                    <span className="detalle-cotizacion__titulo-bloque">Información general</span>
                    <TarjetaInfoGeneral cotizacion={cotizacion} cambiosRuta={cotizacion.cambiosRuta} setCotizacion={setCotizacion} />
                </div>
            )}

            {/* ════════════ BOTÓN PARA COPIAR DATOS A PRODUCCIÓN ════════════ */}
            {visibilidad.enviarAProduccion && permisosFlags.puedeMandarProduccion && (
                <div className="flex flex-row-reverse flex-wrap align-items-center gap-2 my-2">
                    <Button
                        label="Enviar a producción"
                        icon="pi pi-upload"
                        severity="danger"
                        onClick={handleSendToProduction}
                        disabled={!costosDefinidos}
                        tooltip={!costosDefinidos ? mensajeCostosPendientes : undefined}
                        tooltipOptions={{ position: "top" }}
                    />
                    {esReferencia && (
                        <span className="text-sm font-semibold" style={{ color: "#c2410c" }}>
                            <i className="pi pi-lock mr-1" />
                            Precio de referencia: no se puede enviar a producción
                        </span>
                    )}
                    {!costosDefinidos && (
                        <span className="text-sm" style={{ color: "#b91c1c" }}>
                            <i className="pi pi-exclamation-circle mr-1" />
                            {mensajeCostosPendientes}
                        </span>
                    )}
                    {costosDefinidos && pagosNoConcuerdanConSugerido && (
                        <span className="text-sm" style={{ color: "#b45309" }}>
                            <i className="pi pi-exclamation-triangle mr-1" />
                            El pago al chofer no concuerda con el pago sugerido
                        </span>
                    )}
                    {costosDefinidos && !pagosNoConcuerdanConSugerido && pagoChoferDesactualizado && (
                        <span className="text-sm" style={{ color: "#b45309" }}>
                            <i className="pi pi-exclamation-triangle mr-1" />
                            {mensajeCostosPendientes}
                        </span>
                    )}
                </div>
            )}

            {/* ════════════ RUTA ════════════ */}
            {visibilidad.rutas && (
                <div className="detalle-cotizacion__seccion">
                    <span className="detalle-cotizacion__titulo-bloque">Información económica y operativa</span>
                    {!ruta || !ruta.id ? (
                        <div className="detalle-cotizacion__sin-datos">
                            <i className="pi pi-map text-4xl mb-2" style={{ color: "#ccc" }} />
                            <p>Aún no se ha asignado una ruta a esta cotización.</p>
                        </div>
                    ) : (
                        <TarjetaRuta
                            cotizacion={cotizacion}
                            diasSemana={DIAS_SEMANA}
                            visibilidad={visibilidad}
                            onGenerarTrazo={onGenerarTrazo}
                            handleEditarAllTrazos={handleEditarAllTrazos}
                            onEditarTrazo={onEditarTrazo}
                            onValidarEstructuras={handleVerificar}
                            onValidarTrazo={handleVerificarTrazo}
                            setCotizacion={setCotizacion}
                            onSendToClient={handleSendToClient}
                            permisosFlags={permisosFlags}
                            onSolicitarCambios={handleSolicitarCambios}
                        />
                    )}
                </div>
            )}

            <DialogCombinable
                visible={dialogCombinableVisible}
                onHide={() => setDialogCombinableVisible(false)}
                onConfirmar={handleMarcarCombinable}
                idRutaInicial={cotizacion.idRutaCombinada ?? null}
            />
        </div>
    );
};

export default DetallesComponent;
